import React, { useMemo } from "react"
import { Radio } from "antd"
import { compileBase, describeCondition } from "../../compiler/base"
import { compileTable } from "../../compiler/table"
import { describeConditionGroups } from "../../model/conditions"
import { resolveTable } from "../../model/resolve"
import { shownNames } from "../../model/shownLabels"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import { BASE_RULES, BaseRule, TableSpec } from "../../types/tabSpecDocument"
import { BASE_CHOICE_TEXT } from "../Grid/describe"
import ConditionGroups, { DraftGroup } from "./ConditionGroups.component"
import DraftInput from "./DraftInput.component"
import Field from "./Field.component"
import { useInspectedTable } from "./useInspectedTable"

type BaseChoice = BaseRule | "default"

const withoutBaseText = (table: TableSpec): TableSpec => {
  const next = { ...table }
  delete next.baseText
  return next
}

/**
 * Who the table counts: the base rule (the spec's default or the table's
 * own), who is asked the question and the counts that fall outside the base,
 * the base text, the book filter (read only, set under Data) and the
 * table's filter. `filterDraft` keeps the filter's groups as shown, a group
 * not yet filled included, while the tab is not. A project without a
 * questionnaire has no routing: nothing is said of who is asked.
 */
const BaseTab: React.FC<{
  tableId: string
  filterDraft?: { current: DraftGroup[] | null }
}> = ({ tableId, filterDraft }) => {
  const inspected = useInspectedTable(tableId)
  const dataOnly = useTabSpecStore((s) => s.dataOnly)
  const defaultRule = useTabSpecStore((s) => s.document.defaults.base)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  // Every name as the tables show it.
  const labels = useTabSpecStore((s) => s.document.labels)
  const names = useMemo(() => shownNames(labels), [labels])
  const book = useTabSpecStore((s) => s.document.filter)
  const known = useTabSpecStore((s) => s.catalogue)
  const bookWords = useMemo(
    () => (book ? describeConditionGroups(book, names, known ?? undefined) : null),
    [book, names, known]
  )

  const facts = useMemo(() => {
    if (!inspected) return null
    const { table, resolved, document, catalogue } = inspected
    // Who is asked, whatever base the table uses: the routing as the "asked"
    // rule would read it, and why it cannot when it cannot.
    const probe = compileBase({ ...resolved, base: "asked" }, catalogue, names)
    const instance = resolved.instance ?? resolved.question?.instances[0] ?? null
    const condition =
      probe.effective === "asked" && instance?.asked
        ? describeCondition(instance.asked, names.path)
        : null
    const routing =
      probe.effective !== "asked"
        ? (probe.warnings[0] ?? null)
        : condition
          ? null
          : "Everyone is asked this question."
    // The text the table gets without its own: the plan's, which also covers
    // a grid summary's listwise base.
    const plain = resolveTable(withoutBaseText(table), document, catalogue, names)
    const generated =
      compileTable(plain, document, catalogue, names).baseText ||
      compileBase(plain, catalogue, names).baseText
    return { instance, condition, routing, generated }
  }, [inspected, names])

  if (!inspected || !facts) return null
  const { table, resolved, catalogue } = inspected
  const { instance, condition, routing, generated } = facts
  // A variable from the data, or any table of a project without a
  // questionnaire, has no routing: "Who was asked" is not offered, and a
  // default of "asked" runs on who answered (compileBase says so in the
  // facts, or silently without a questionnaire). A stored "asked" matches no
  // choice and ticks none. The Defaults drawer and the bulk bar agree.
  const noRouting = table.source === "data" || dataOnly
  const rules = noRouting ? BASE_RULES.filter((b) => b !== "asked") : BASE_RULES
  const shownDefault = noRouting && defaultRule === "asked" ? "answered" : defaultRule
  const choices: { value: BaseChoice; label: string }[] = [
    { value: "default", label: `Default (${BASE_CHOICE_TEXT[shownDefault]})` },
    ...rules.map((b) => ({ value: b, label: BASE_CHOICE_TEXT[b] })),
  ]
  const offPath = instance?.offPath ?? 0
  const missing = instance?.missingOnPath ?? 0

  return (
    <div className="ts-tab">
      <Field label="Base">
        <Radio.Group
          className="ts-base__choices"
          aria-label="Base"
          value={table.base ?? "default"}
          options={choices}
          onChange={(e) => {
            const choice = e.target.value as BaseChoice
            if (choice === "default") clearOverrides(tableId, ["base"])
            else updateTable(tableId, { base: choice })
          }}
        />
        {resolved.question && !dataOnly && (
          <div className="ts-base__facts">
            {condition ? (
              <p className="ts-base__condition">
                Asked when <strong>{condition}</strong>
              </p>
            ) : (
              routing && <p className="ts-hint">{routing}</p>
            )}
            {offPath > 0 && <p>{offPath} answered although they were not asked.</p>}
            {missing > 0 && (
              <p>
                {missing} {missing === 1 ? "was" : "were"} asked and gave no answer.
              </p>
            )}
          </div>
        )}
      </Field>
      <Field label="Base text">
        <DraftInput
          ariaLabel="Base text"
          placeholder={generated}
          value={table.baseText ?? ""}
          onCommit={(text) =>
            text.trim()
              ? updateTable(tableId, { baseText: text.trim() })
              : clearOverrides(tableId, ["baseText"])
          }
        />
      </Field>
      {bookWords && (
        <Field label="Book filter">
          <p className="ts-base__condition">{bookWords}</p>
          <p className="ts-hint">
            Every table in this tab spec counts only these respondents. It is set under Data.
          </p>
        </Field>
      )}
      <Field label="Filter">
        <ConditionGroups
          value={table.filter}
          onChange={(filter) => updateTable(tableId, { filter })}
          catalogue={catalogue}
          names={names}
          draft={filterDraft}
        />
      </Field>
    </div>
  )
}

export default BaseTab
